import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Ruler, Calculator, FileText, Brain, ArrowRight, Mail, Lock, User, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { authFetch } from "@/lib/auth";
import ForgotPassword from "@/components/ForgotPassword";
import ResetPassword from "@/components/ResetPassword";
import GrupoVentasuelos from "@/components/GrupoVentasuelos";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Landing() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showLogin, setShowLogin] = useState(false);
  const [authMode, setAuthMode] = useState("login"); // "login", "register", "forgot", "reset"
  const [authForm, setAuthForm] = useState({ email: "", password: "", name: "" });
  const [authLoading, setAuthLoading] = useState(false);

  const resetToken = searchParams.get("reset_token");

  useEffect(() => {
    if (resetToken) {
      setShowLogin(true);
      setAuthMode("reset");
    }
  }, [resetToken]);

  useEffect(() => {
    if (user) navigate("/dashboard", { replace: true });
  }, [user, navigate]);

  if (user) return null;

  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleEmailAuth = async (e) => {
    e.preventDefault();
    if (!authForm.email || !authForm.password) {
      toast.error("Rellena todos los campos");
      return;
    }
    if (authMode === "register" && !authForm.name) {
      toast.error("Introduce tu nombre");
      return;
    }
    setAuthLoading(true);
    try {
      const endpoint = authMode === "register" ? "/auth/register" : "/auth/login";
      const body = authMode === "register"
        ? { email: authForm.email, password: authForm.password, name: authForm.name }
        : { email: authForm.email, password: authForm.password };
      
      const res = await authFetch(`${API}${endpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      
      if (res.ok) {
        const userData = await res.json();
        setUser(userData);
        navigate("/dashboard", { replace: true });
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Error de autenticacion");
      }
    } catch {
      toast.error("Error de conexion");
    } finally {
      setAuthLoading(false);
    }
  };

  const features = [
    { icon: Brain, title: "Medición Inteligente", desc: "Sube tu plano o introduce medidas manualmente. La IA te ayuda a detectar estancias automáticamente." },
    { icon: Calculator, title: "Cálculos Completos", desc: "Materiales (8 tipos), adhesivos específicos por instalación y herramientas necesarias. Todo con margen de desperdicio incluido." },
    { icon: FileText, title: "Informes Técnicos PDF", desc: "Genera documentos profesionales con el desglose completo de materiales, adhesivos y herramientas necesarias." },
  ];

  return (
    <div className="min-h-screen bg-[#F0F4F8]">
      {/* Header */}
      <header className="bg-white border-b border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2" data-testid="logo-text">
            <img src="/planimetrik-header.png" alt="Planimetrik" className="h-8 object-contain" />
          </div>
          <Button
            data-testid="header-login-btn"
            onClick={() => setShowLogin(true)}
            className="bg-[#0066FF] hover:bg-[#0052CC] text-white font-medium"
          >
            Iniciar Sesión
          </Button>
        </div>
      </header>

      {/* Hero - Full width background image */}
      <section className="w-full">
        <div className="w-full flex justify-center bg-[#F0F4F8]">
          <img
            src="/planimetrik-fondo.jpg"
            alt="Planimetrik fondo"
            className="w-full h-auto"
            style={{ maxWidth: "1327px" }}
            data-testid="hero-bg-image"
          />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-10 text-center animate-fade-in-up">
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-[#1B2B4D] leading-tight">
            Calcula materiales de pavimento en segundos con{" "}
            <span className="text-[#0066FF]">precisión profesional</span>
          </h1>
          <p className="mt-4 text-base md:text-lg text-[#4A5568] max-w-2xl mx-auto">
            La herramienta profesional para instaladores, arquitectos y constructoras.
            Sube un plano, selecciona materiales y recibe al instante: cantidades exactas, adhesivos necesarios y listado completo de herramientas.
          </p>
          <div className="mt-6">
            <Button
              data-testid="hero-start-btn"
              onClick={() => setShowLogin(true)}
              className="bg-[#0066FF] hover:bg-[#0052CC] text-white font-medium h-12 px-8 text-base"
            >
              Empezar Gratis
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <div className="grid sm:grid-cols-3 gap-5">
          {features.map((f, i) => (
            <Card key={i} className="bg-white rounded-xl border border-[#E2E8F0] shadow-sm hover:shadow-md transition-shadow duration-200" data-testid={`feature-card-${i}`}>
              <CardContent className="p-5 md:p-6">
                <div className="w-11 h-11 rounded-lg bg-[#E3F2FD] flex items-center justify-center mb-4">
                  <f.icon className="h-5 w-5 text-[#0066FF]" />
                </div>
                <h3 className="font-heading text-lg font-semibold text-[#1B2B4D] mb-2">{f.title}</h3>
                <p className="text-sm text-[#4A5568]">{f.desc}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* Login Modal */}
      {showLogin && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setShowLogin(false)}>
          <Card className="w-full max-w-md bg-white rounded-xl animate-fade-in-up" onClick={(e) => e.stopPropagation()}>
            <CardContent className="p-6 md:p-8">
              <div className="flex items-center justify-center mb-4">
                <img src="/planimetrik-header.png" alt="Planimetrik" className="h-8 object-contain" />
              </div>

              {authMode === "forgot" ? (
                <ForgotPassword onBack={() => setAuthMode("login")} />
              ) : authMode === "reset" ? (
                <ResetPassword token={resetToken} onComplete={() => { setSearchParams({}); setAuthMode("login"); }} />
              ) : (
                <>
                <p className="text-sm text-[#4A5568] text-center mb-4">
                  {authMode === "login" ? "Inicia sesión para continuar" : "Crea tu cuenta gratuita"}
                </p>

              <Button
                data-testid="google-login-btn"
                onClick={handleGoogleLogin}
                className="w-full h-11 bg-white border border-[#E2E8F0] text-[#1B2B4D] hover:bg-[#F0F4F8] font-medium mb-4"
              >
                <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                </svg>
                Continuar con Google
              </Button>

              <div className="flex items-center gap-3 my-4">
                <Separator className="flex-1" />
                <span className="text-xs text-[#4A5568]">o con email</span>
                <Separator className="flex-1" />
              </div>

              <form onSubmit={handleEmailAuth} className="space-y-3">
                {authMode === "register" && (
                  <div>
                    <Label className="text-sm font-semibold text-[#1B2B4D] mb-1 block">Nombre</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#CBD5E0]" />
                      <Input
                        data-testid="register-name-input"
                        type="text"
                        placeholder="Tu nombre"
                        value={authForm.name}
                        onChange={(e) => setAuthForm({ ...authForm, name: e.target.value })}
                        className="h-11 pl-10 border-[#CBD5E0]"
                      />
                    </div>
                  </div>
                )}
                <div>
                  <Label className="text-sm font-semibold text-[#1B2B4D] mb-1 block">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#CBD5E0]" />
                    <Input
                      data-testid="email-input"
                      type="email"
                      placeholder="tu@email.com"
                      value={authForm.email}
                      onChange={(e) => setAuthForm({ ...authForm, email: e.target.value })}
                      className="h-11 pl-10 border-[#CBD5E0]"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-sm font-semibold text-[#1B2B4D] mb-1 block">Contraseña</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#CBD5E0]" />
                    <Input
                      data-testid="password-input"
                      type="password"
                      placeholder="Min. 6 caracteres"
                      value={authForm.password}
                      onChange={(e) => setAuthForm({ ...authForm, password: e.target.value })}
                      className="h-11 pl-10 border-[#CBD5E0]"
                    />
                  </div>
                </div>
                {authMode === "login" && (
                  <div className="text-right">
                    <button
                      type="button"
                      onClick={() => setAuthMode("forgot")}
                      className="text-xs text-[#0066FF] hover:underline"
                      data-testid="forgot-password-link"
                    >
                      ¿Olvidaste tu contraseña?
                    </button>
                  </div>
                )}
                <Button
                  type="submit"
                  disabled={authLoading}
                  data-testid="email-auth-submit-btn"
                  className="w-full h-11 bg-[#0066FF] hover:bg-[#0052CC] text-white font-medium"
                >
                  {authLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  {authMode === "login" ? "Iniciar sesión" : "Crear cuenta"}
                </Button>
              </form>

              <p className="text-center text-xs text-[#4A5568] mt-4">
                {authMode === "login" ? (
                  <>No tienes cuenta? <button onClick={() => setAuthMode("register")} className="text-[#0066FF] font-medium hover:underline" data-testid="switch-to-register">Regístrate</button></>
                ) : (
                  <>¿Ya tienes cuenta? <button onClick={() => setAuthMode("login")} className="text-[#0066FF] font-medium hover:underline" data-testid="switch-to-login">Inicia sesión</button></>
                )}
              </p>
              </>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Grupo Ventasuelos */}
      <GrupoVentasuelos />

      {/* Footer */}
      <footer className="bg-[#F0F4F8] py-6">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p className="text-sm text-[#4A5568]">Planimetrik &copy; {new Date().getFullYear()} - Todos los derechos reservados.</p>
          <p className="text-xs text-[#718096] mt-1">A Ventasuelos SL Company</p>
        </div>
      </footer>
    </div>
  );
}
